import React from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { CRMPageWrap } from "../../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMPageBody } from "../../components/Simple/CRMPageBody/CRMPageBody";
import { CRMLegalMenuBarContainer } from "../../containers/CRMLegalMenuBarContainer/CRMLegalMenuBarContainer";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import { FontQuicksand } from "../../components/BuildingBlocks/FontQuicksand";
import { FontSize } from "../../components/BuildingBlocks/FontSize";
import { CRMFontSizes } from "../../models/CRMFontSizes";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { NpsDashboardResponseCodec, toCsv, TRow, TSummary } from "./npsDashboardResponse";

type TFilters = {
    from_date: string;
    to_date: string;
    score_band: string;
    respondent_type: string;
    introducer_id: string;
};

type TState = {
    filters: TFilters;
    rows: TRow[];
    summary: TSummary | null;
    loading: boolean;
    error: string | null;
};

const initialFilters = (queryParams: { from_date?: string; to_date?: string; score_band?: string; respondent_type?: string; introducer_id?: string }): TFilters => ({
    from_date: queryParams.from_date || "",
    to_date: queryParams.to_date || "",
    score_band: queryParams.score_band || "",
    respondent_type: queryParams.respondent_type || "",
    introducer_id: queryParams.introducer_id || "",
});

const buildQueryString = (f: TFilters): string => {
    const params: string[] = [];
    if (f.from_date) params.push(`from_date=${encodeURIComponent(f.from_date)}`);
    if (f.to_date) params.push(`to_date=${encodeURIComponent(f.to_date)}`);
    if (f.score_band) params.push(`score_band=${encodeURIComponent(f.score_band)}`);
    if (f.respondent_type) params.push(`respondent_type=${encodeURIComponent(f.respondent_type)}`);
    if (f.introducer_id) params.push(`introducer_id=${encodeURIComponent(f.introducer_id)}`);
    return params.length ? `?${params.join("&")}` : "";
};

const scoreBand = (score: number): "promoter" | "passive" | "detractor" =>
    score >= 9 ? "promoter" : score >= 7 ? "passive" : "detractor";

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return Number.isFinite(d.getTime())
        ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        : iso;
};

const downloadCsv = (rows: TRow[]): void => {
    const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `nps-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export class CRMNpsPage extends React.Component<React.PropsWithChildren<TContainerStateProps>, TState> {
    public state: TState = {
        filters: initialFilters(this.props.state.routes.queryParams.VIEW_CRM_NPS),
        rows: [],
        summary: null,
        loading: true,
        error: null,
    };

    componentDidMount(): void {
        this.fetchData();
    }

    private fetchData = (): void => {
        this.setState({ loading: true, error: null });
        fetch(`${env.REACT_APP_API_URL}/web/nps/dashboard${buildQueryString(this.state.filters)}`, {
            credentials: "include",
            headers: { "accept": "application/json", "content-type": "application/json" },
        })
            .then((r) => {
                if (!r.ok) throw new Error(`status ${r.status}`);
                return r.json();
            })
            .then((raw: unknown) => {
                const decoded = NpsDashboardResponseCodec.decode(raw);
                if (decoded._tag !== "Right") throw new Error("NPS dashboard response failed schema validation");
                return decoded.right;
            })
            .then((data) => {
                this.setState({ rows: data.rows, summary: data.summary, loading: false });
            })
            .catch((e: Error) => {
                this.setState({ loading: false, error: e.message });
            });
    };

    private setFilter = <K extends keyof TFilters>(key: K, value: TFilters[K]): void => {
        this.setState({ filters: { ...this.state.filters, [key]: value } });
    };

    public render(): JSX.Element {
        const { filters, rows, summary, loading, error } = this.state;

        return (
            <div className="crm-nps-page-wrap">
                <CRMPageWrap>
                    <CRMLegalMenuBarContainer {...this.props} />
                    <CRMPageBody>
                        <CRMPadding size="large">
                            <CRMSpacingColumn spacing="large">
                                <FontQuicksand>
                                    <FontSize size={CRMFontSizes.XX_LARGE}>
                                        <WeightBold>NPS Dashboard</WeightBold>
                                    </FontSize>
                                </FontQuicksand>

                                <div className="crm-nps-page">
                                    {/* KPI STRIP */}
                                    {summary &&
                                        <div className="crm-nps-page__kpis">
                                            <div className="crm-nps-page__kpi">
                                                <p className="crm-nps-page__kpi-label">NPS</p>
                                                <p className="crm-nps-page__kpi-value">{summary.nps_score}</p>
                                            </div>
                                            <div className="crm-nps-page__kpi">
                                                <p className="crm-nps-page__kpi-label">Response rate</p>
                                                <p className="crm-nps-page__kpi-value">{summary.response_rate}%</p>
                                            </div>
                                            <div className="crm-nps-page__kpi">
                                                <p className="crm-nps-page__kpi-label">Responses</p>
                                                <p className="crm-nps-page__kpi-value">{summary.total_responses}</p>
                                            </div>
                                            <div className="crm-nps-page__kpi">
                                                <p className="crm-nps-page__kpi-label">Promoters / Passives / Detractors</p>
                                                <p className="crm-nps-page__kpi-value">
                                                    {summary.promoters}/{summary.passives}/{summary.detractors}
                                                </p>
                                            </div>
                                        </div>
                                    }

                                    {/* FILTERS */}
                                    <div className="crm-nps-page__filters">
                                        <div>
                                            <label className="crm-nps-page__filter-label">From</label>
                                            <input
                                                className="crm-nps-page__filter-input"
                                                type="date"
                                                value={filters.from_date}
                                                onChange={(e) => this.setFilter("from_date", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="crm-nps-page__filter-label">To</label>
                                            <input
                                                className="crm-nps-page__filter-input"
                                                type="date"
                                                value={filters.to_date}
                                                onChange={(e) => this.setFilter("to_date", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="crm-nps-page__filter-label">Score band</label>
                                            <select
                                                className="crm-nps-page__filter-select"
                                                value={filters.score_band}
                                                onChange={(e) => this.setFilter("score_band", e.target.value)}
                                            >
                                                <option value="">All</option>
                                                <option value="promoter">Promoter (9-10)</option>
                                                <option value="passive">Passive (7-8)</option>
                                                <option value="detractor">Detractor (0-6)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="crm-nps-page__filter-label">Respondent</label>
                                            <select
                                                className="crm-nps-page__filter-select"
                                                value={filters.respondent_type}
                                                onChange={(e) => this.setFilter("respondent_type", e.target.value)}
                                            >
                                                <option value="">All</option>
                                                <option value="consumer">Consumer</option>
                                                <option value="introducer">Introducer</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="crm-nps-page__filter-label">&nbsp;</label>
                                            <button
                                                className="crm-nps-page__filter-input"
                                                type="button"
                                                onClick={this.fetchData}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>

                                    {/* ACTIONS */}
                                    <div className="crm-nps-page__actions-row">
                                        <a
                                            className="crm-nps-page__csv-link"
                                            href="#"
                                            onClick={(e) => { e.preventDefault(); downloadCsv(rows); }}
                                        >
                                            Download CSV
                                        </a>
                                    </div>

                                    {/* TABLE */}
                                    {loading
                                        ? <p className="crm-nps-page__empty">Loading…</p>
                                        : error
                                            ? <p className="crm-nps-page__empty">Couldn't load NPS data ({error}).</p>
                                            : rows.length === 0
                                                ? <p className="crm-nps-page__empty">No responses match these filters.</p>
                                                : <table className="crm-nps-page__table">
                                                    <thead>
                                                        <tr>
                                                            <th>Date</th>
                                                            <th>Score</th>
                                                            <th>Type</th>
                                                            <th>Name</th>
                                                            <th>Case</th>
                                                            <th>Introducer</th>
                                                            <th>Sold by</th>
                                                            <th>Feedback</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {rows.map((r, i) => (
                                                            <tr key={i}>
                                                                <td>{formatDate(r.nps_answered_at)}</td>
                                                                <td>
                                                                    <span className={`crm-nps-page__score crm-nps-page__score--${scoreBand(r.nps_score)}`}>
                                                                        {r.nps_score}
                                                                    </span>
                                                                </td>
                                                                <td>{r.user_type === "introducer" ? "Introducer" : "Consumer"}</td>
                                                                <td>{`${r.user_first_name} ${r.user_last_name}`.trim()}</td>
                                                                <td>{r.case_address_short_string}</td>
                                                                <td>{r.introducer_name || ""}</td>
                                                                <td>
                                                                    {r.sold_by_sail_homes
                                                                        ? <span className="crm-nps-page__tag">Sail Homes</span>
                                                                        : ""
                                                                    }
                                                                </td>
                                                                <td>{r.nps_feedback_text}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                    }
                                </div>
                            </CRMSpacingColumn>
                        </CRMPadding>
                    </CRMPageBody>
                </CRMPageWrap>
            </div>
        );
    }
}
